import React from 'react';
import styled, { css } from 'styled-components';
import theme from 'theme';

import { LogoIcon } from 'components/icons/LogoIcon';
import SectionTitle from 'components/previewPages/SectionTitle';

import Header from '../../components/Header';
import HeroSection from '../../components/HeroSection';

type Props = {
  image: string | null;
  title: string;
  intro: string;
  body: string;
  isMobile: boolean;
  isTablet: boolean;
  onTryForFreeClick: () => void;
};

const IntroSection = ({
  image,
  title,
  intro,
  body,
  isMobile,
  isTablet,
  onTryForFreeClick,
}: Props) => (
  <Container>
    <Header
      isMobile={isMobile}
      right={
        <TryForFree
          $color={theme.colors.white}
          $bgColor={theme.colors.darkBlueV2}
          role="button"
          onClick={onTryForFreeClick}
        >
          Try for free
        </TryForFree>
      }
    />
    <HeroSection title={title} isMobile={isMobile} isTablet={isTablet}>
      <ImageGraphContainer $isMobile={isMobile}>
        <ImageInnerContainer $isMobile={isMobile}>
          <AdditionalImageInner $isMobile={isMobile}>
            {image ? (
              <img src={image} alt={title} />
            ) : (
              <LogoIcon
                width={isMobile ? 100 : 153}
                height={isMobile ? 50 : 93}
              />
            )}
          </AdditionalImageInner>
        </ImageInnerContainer>
      </ImageGraphContainer>
    </HeroSection>

    <IntroContent $isMobile={isMobile}>
      <SectionTitle isMobile={isMobile}>{intro}</SectionTitle>
      <IntroBody>{body}</IntroBody>
    </IntroContent>
  </Container>
);

const Container = styled.div`
  overflow: hidden;
`;

const TryForFree = styled.span<{
  $color: string;
  $bgColor: string;
}>`
  font-size: 16px;
  color: ${({ $color }) => $color};
  background-color: ${({ $bgColor }) => $bgColor};
  cursor: pointer;
  border-radius: 20px;
  padding: 12px 20px;
  border: none;
  font-weight: 400;
  text-decoration: none;
  box-shadow: 0 10px 20px rgb(0 0 0 / 10%);
`;

const ImageGraphContainer = styled.div<{ $isMobile: boolean }>`
  width: 300px;
  height: 300px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed ${({ theme }) => theme.colors.lightBlue};
  border-radius: 50%;
  position: absolute;
  bottom: -50%;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 3;
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      width: 150px;
      height: 150px;
      bottom: -22%;
    `}
`;

const ImageInnerContainer = styled.div<{ $isMobile: boolean }>`
  width: 270px;
  height: 270px;
  box-sizing: border-box;
  border: 2px solid ${({ theme }) => theme.colors.funtainBlue};
  background: white;
  border-radius: 50%;
  padding: 1px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: -1px 1px 15px -4px grey;
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      width: 116px;
      height: 116px;
    `}

  svg {
    fill: ${({ theme }) => theme.colors.primary};
  }
`;

const AdditionalImageInner = styled.div<{ $isMobile: boolean }>`
  width: 262px;
  height: 262px;
  background: white;
  border-radius: 50%;
  box-sizing: border-box;
  padding: 1px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      width: 109px;
      height: 109px;
    `}

  svg {
    fill: ${({ theme }) => theme.colors.primary};
  }

  img {
    width: 268px;
    height: 268px;
    object-fit: cover;
    ${({ $isMobile }) =>
      $isMobile &&
      css`
        width: 116px;
        height: 116px;
      `}
  }
`;

const IntroContent = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  border-bottom: 1px dashed rgba(65 78 113 / 25%);
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 220px;
  padding-bottom: 78px;
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      margin-top: 100px;
      padding-bottom: 34px;
    `}
`;

const IntroBody = styled.p`
  color: ${({ theme }) => theme.colors.topicBlue};
  font-size: 20px;
  font-weight: 300;
  line-height: 35px;
  text-align: center;
  margin: 0;
`;

export default IntroSection;
